import { buildQueryParams, toQueryParams } from '@/helpers/ApiHelper'
import type { IPaginationOptions } from '@/misc/IPaginationOptions'
import type { IAuthorisationFilterParams } from '@/models/Authorisation/Filtering/IAuthorisationFilterParams'
import type { IAuthorisation } from '@/models/Authorisation/IAuthorisation'
import type { IAuthorisationTokenRequest } from '@/models/Authorisation/IAuthorisationTokenRequest'
import type { IAuthorisationTokenResponse } from '@/models/Authorisation/IAuthorisationTokenResponse'
import type { IExcelColumn } from '@/models/Filtering/ExcelExportFilter'
import type { IContentPage } from '@/models/Pagination/IContentPage'
import { inject, injectable } from 'inversify'
import type IApiClient from '../IApiClient'
import type { IApiRequestOptions } from '../IApiRequestOptions'
import { SERVICES_TYPES } from '../ServicesTypes'
import type IAuthorisationService from './IAuthorisationService'

@injectable()
export class AuthorisationService implements IAuthorisationService {
  private readonly apiClient: IApiClient
  private readonly authorisationPrefix: string = 'authorisations'

  constructor(@inject(SERVICES_TYPES.ApiClient) apiClient: IApiClient) {
    this.apiClient = apiClient
  }

  public async getAuthorisations(
    paginationOptions: IPaginationOptions,
    sortField?: string,
    isAscending?: boolean,
    filterParams?: IAuthorisationFilterParams,
    abortController?: AbortController
  ): Promise<IContentPage<IAuthorisation>> {
    const queryString = buildQueryParams({
      pageIndex: paginationOptions.PageNumber,
      pageSize: paginationOptions.PageSize,
      sortColumn: sortField,
      sortReversed: !isAscending,
      authorisationReference: filterParams?.AuthorisationReference,
      dateFrom: filterParams?.DateFrom,
      dateUntil: filterParams?.DateUntil,
      externalSessionId: filterParams?.ExternalSessionId,
      initiator: filterParams?.Initiator?.toString(),
      chargePointId: filterParams?.ChargePointId,
      tokenId: filterParams?.TokenId,
      visualId: filterParams?.VisualId,
      tokenType: filterParams?.TokenType?.toString(),
      evcoId: filterParams?.EvcoId,
      isAuthorised: filterParams?.IsAuthorised
    })

    return this.apiClient.get<IContentPage<IAuthorisation>>(`${this.authorisationPrefix}${queryString}`, abortController)
  }

  public async getAuthorisation(id: string, abortController?: AbortController, options?: IApiRequestOptions): Promise<IAuthorisation> {
    return this.apiClient.get<IAuthorisation>(`${this.authorisationPrefix}/${id}`, abortController, options)
  }

  public async authorizeToken(authorisationRequest: IAuthorisationTokenRequest, abortController?: AbortController): Promise<IAuthorisationTokenResponse> {
    const queryParams = toQueryParams(authorisationRequest)
    return this.apiClient.postNoBody(`authorisation/${authorisationRequest.TokenType}/${authorisationRequest.TokenId}/${authorisationRequest.ChargePointId}?${queryParams}`, abortController)
  }

  public async exportAuthorisations(
    size: number,
    columnList: IExcelColumn[],
    paginationOptions: IPaginationOptions,
    sortField?: string,
    isAscending?: boolean,
    filterParams?: IAuthorisationFilterParams,
    abortController?: AbortController
  ): Promise<{ blob: Blob; fileName?: string }> {
    const queryString = buildQueryParams({
      pageIndex: paginationOptions.PageNumber,
      pageSize: paginationOptions.PageSize,
      sortColumn: sortField,
      sortReversed: !isAscending,
      authorisationReference: filterParams?.AuthorisationReference,
      dateFrom: filterParams?.DateFrom,
      dateUntil: filterParams?.DateUntil,
      externalSessionId: filterParams?.ExternalSessionId,
      initiator: filterParams?.Initiator?.toString(),
      chargePointId: filterParams?.ChargePointId,
      tokenId: filterParams?.TokenId,
      visualId: filterParams?.VisualId,
      tokenType: filterParams?.TokenType?.toString(),
      evcoId: filterParams?.EvcoId,
      isAuthorised: filterParams?.IsAuthorised
    })

    return this.apiClient.postBlobWithFileName(`${this.authorisationPrefix}/download/${size}${queryString}`, columnList, abortController)
  }
}
